import { useCallback, useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Button, Card, Checkbox, Field, Input, NativeSelect, Spinner, StatusPill } from '../../../ui'
import { SEND_KIND_LABEL, sendKindLabel } from '../commsInboxView/shared'
import { LETTER_LINKING_EXPLAINER } from '../commsShared/linkStates'
import { MailboxRef, mailboxOptions } from '../commsShared/mailboxes'
import { ExportRequest, downloadCommsExport } from '../commsShared/exportDownload'
import styles from '../commsShared/admin.module.scss'

interface CategoryRow {
    key: string
    label: string
    total: number
    claims: number
    linked: number
    lastReceivedAt: string | null
}

interface LetterTypeRow {
    key: string
    label: string
    emails: number
    claims: number
    categories: { key: string; label: string; emails: number; claims: number; lastReceivedAt: string | null }[]
}

interface Filters {
    mailbox: string
    from: string
    to: string
    includeArchived: boolean
    /** Emails not yet linked to a claim appear as one row each, with what the model extracted. */
    includeUnlinked: boolean
}

const EMPTY_FILTERS: Filters = { mailbox: '', from: '', to: '', includeArchived: false, includeUnlinked: true }

/**
 * Comms management exports. Two ways to cut the same data, both as .xlsx with
 * one row per client/claim carrying the latest email in the group (and, by
 * default, one row per email that is not yet linked to a claim):
 *   by category                  "every claim whose lender has made a settlement offer"
 *   by letter type and category  "every claim where a chaser was answered with an acknowledgement"
 * Filters (mailbox, dates, archived) apply to both tables and to the files.
 */
export const ExportsSection = () => {
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
    const [mailboxes, setMailboxes] = useState<MailboxRef[]>([])
    const [categories, setCategories] = useState<CategoryRow[]>([])
    const [letterTypes, setLetterTypes] = useState<LetterTypeRow[]>([])
    const [matrixCategories, setMatrixCategories] = useState<{ key: string; label: string }[]>([])
    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState<string | null>(null)

    const apiFilters = useMemo(
        () => ({ mailbox: filters.mailbox || null, from: filters.from || null, to: filters.to || null, includeArchived: filters.includeArchived, includeUnlinked: filters.includeUnlinked }),
        [filters]
    )

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const sinceDays = filters.from ? Math.max(1, Math.ceil((Date.now() - new Date(filters.from).getTime()) / 86_400_000)) : null
            const [cat, lt, mb] = await Promise.all([
                api({ url: 'comms/inbox/category-report', data: { sourceMailbox: filters.mailbox || null, sinceDays } }),
                api({ url: 'comms/inbox/letter-type-report', data: apiFilters }),
                mailboxes.length ? null : api({ url: 'comms/mailboxes/list', data: {} }).catch(() => null),
            ])
            if (cat.data?.success) {
                setCategories(
                    (cat.data.categories || [])
                        .filter((c: any) => c.total > 0)
                        .map((c: any) => ({ key: c.key === '__uncategorised' ? 'unclassified' : c.key, label: c.label, total: c.total, claims: c.claims, linked: c.linked, lastReceivedAt: c.lastReceivedAt }))
                )
            }
            if (lt.data?.success) {
                setLetterTypes(lt.data.letterTypes || [])
                setMatrixCategories(lt.data.categories || [])
            }
            if (mb?.data?.success) setMailboxes(mb.data.mailboxes || [])
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load the export figures.', 'Okay')
        } finally {
            setLoading(false)
        }
    }, [apiFilters, filters.from, filters.mailbox, mailboxes.length])

    useEffect(() => {
        load()
    }, [load])

    const download = async (label: string, body: Record<string, unknown>) => {
        setDownloading(label)
        try {
            await downloadCommsExport({ kind: 'category', ...body, ...apiFilters } as ExportRequest, `${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.xlsx`)
        } finally {
            setDownloading(null)
        }
    }

    const fmt = (n: number | undefined) => (n ? n.toLocaleString() : '0')
    const kindOrder = Object.keys(SEND_KIND_LABEL)
    const sortedLetterTypes = [...letterTypes].sort((a, b) => kindOrder.indexOf(a.key) - kindOrder.indexOf(b.key))
    const cell = (lt: LetterTypeRow, catKey: string) => lt.categories.find((c) => c.key === catKey)

    return (
        <>
            <Card
                as="section"
                title="Export clients and claims"
                subtitle="Spreadsheets with one row per client and claim: who the client is, the lender, the latest email in the category, the letters we sent and a link back to the conversation. The filters below apply to every export on this page."
                actions={
                    <div className={styles.actions}>
                        <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
                            Refresh figures
                        </Button>
                    </div>
                }
            >
                <div className={styles.exportFilters}>
                    <Field label="Mailbox" inline>
                        {(id) => <NativeSelect id={id} small value={filters.mailbox} onChange={(e) => setFilters({ ...filters, mailbox: e.target.value })} options={[{ value: '', label: 'All mailboxes' }, ...mailboxOptions(mailboxes)]} />}
                    </Field>
                    <Field label="Received from" inline>
                        {(id) => <Input id={id} small type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />}
                    </Field>
                    <Field label="to" inline>
                        {(id) => <Input id={id} small type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />}
                    </Field>
                    <Checkbox label="Include archived emails" hint="Noise and filed emails" checked={filters.includeArchived} onChange={(e) => setFilters({ ...filters, includeArchived: e.target.checked })} />
                    <Checkbox label="Include emails not yet linked to a claim" hint="One row per email, with the details the model read from it" checked={filters.includeUnlinked} onChange={(e) => setFilters({ ...filters, includeUnlinked: e.target.checked })} />
                    {(filters.mailbox || filters.from || filters.to || filters.includeArchived || !filters.includeUnlinked) && (
                        <Button size="sm" variant="link" onClick={() => setFilters(EMPTY_FILTERS)}>
                            Clear filters
                        </Button>
                    )}
                </div>

                <h3 className={styles.subheading}>By category</h3>
                <p className={styles.note}>One row per client/claim whose latest email in that category matches the filters (a claim with several emails in the category appears once, with the most recent), plus one row per email not yet linked to a claim, carrying the client, vehicle and agreement details the model read from it. Linked rows also carry every letter we have sent on the claim.</p>
                {loading && !categories.length ? (
                    <Spinner block text="Counting…" />
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Emails</th>
                                    <th>Clients / claims</th>
                                    <th>Latest</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((c) => (
                                    <tr key={c.key}>
                                        <td>{c.label}</td>
                                        <td>{fmt(c.total)}</td>
                                        <td>{fmt(c.claims)}</td>
                                        <td className={styles.sub}>{c.lastReceivedAt ? new Date(c.lastReceivedAt).toLocaleDateString('en-GB') : '—'}</td>
                                        <td>
                                            <div className={styles.rowActions}>
                                                <Button size="sm" disabled={downloading !== null || !(filters.includeUnlinked ? c.total : c.claims)} loading={downloading === `cat:${c.key}`} onClick={() => download(`cat:${c.key}`, { kind: 'category', category: c.key })} title={`Download every "${c.label}" email with its client and claim as .xlsx`}>
                                                    Export .xlsx
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td>
                                        <strong>All categories</strong>
                                    </td>
                                    <td colSpan={3} className={styles.sub}>
                                        One row per client/claim and category
                                    </td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            <Button size="sm" variant="neutral" disabled={downloading !== null} loading={downloading === 'cat:all'} onClick={() => download('cat:all', { kind: 'category' })}>
                                                Export .xlsx
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card as="section" title="Letter linking" subtitle={LETTER_LINKING_EXPLAINER}>
                <p className={styles.note}>
                    Each cell counts emails (and the clients/claims they belong to) that answered that <strong>type of letter</strong> and landed in that <strong>category</strong>. Click a cell to export those clients and claims. "Not about a specific letter" holds emails linked to a claim with no letter chosen.
                </p>
                {loading && !letterTypes.length ? (
                    <Spinner block text="Counting…" />
                ) : !letterTypes.length ? (
                    <p className={styles.sub}>No linked emails match the filters.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Letter type</th>
                                    <th>Emails</th>
                                    <th>Claims</th>
                                    {matrixCategories.map((c) => (
                                        <th key={c.key}>{c.label}</th>
                                    ))}
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedLetterTypes.map((lt) => (
                                    <tr key={lt.key}>
                                        <td>
                                            <strong>{sendKindLabel(lt.key)}</strong>
                                        </td>
                                        <td>{fmt(lt.emails)}</td>
                                        <td>{fmt(lt.claims)}</td>
                                        {matrixCategories.map((c) => {
                                            const v = cell(lt, c.key)
                                            const id = `lt:${lt.key}:${c.key}`
                                            return (
                                                <td key={c.key}>
                                                    {v ? (
                                                        <Button size="sm" variant="link" disabled={downloading !== null} loading={downloading === id} onClick={() => download(id, { kind: 'letter_type_category', sendKind: lt.key, category: c.key })} title={`Export ${fmt(v.claims)} client/claim row(s): ${sendKindLabel(lt.key)} → ${c.label}`}>
                                                            {fmt(v.claims)}
                                                            <span className={styles.sub}> / {fmt(v.emails)}</span>
                                                        </Button>
                                                    ) : (
                                                        <span className={styles.sub}>—</span>
                                                    )}
                                                </td>
                                            )
                                        })}
                                        <td>
                                            <div className={styles.rowActions}>
                                                <Button size="sm" disabled={downloading !== null} loading={downloading === `lt:${lt.key}`} onClick={() => download(`lt:${lt.key}`, { kind: 'letter_type_category', sendKind: lt.key })} title={`Every client/claim that answered a ${sendKindLabel(lt.key).toLowerCase()}, one row per category`}>
                                                    Export .xlsx
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className={styles.actions} style={{ marginTop: 12 }}>
                    <StatusPill size="sm">claims / emails per cell</StatusPill>
                    <Button size="sm" variant="neutral" disabled={downloading !== null} loading={downloading === 'lt:all'} onClick={() => download('lt:all', { kind: 'letter_type_category' })}>
                        Export the whole matrix .xlsx
                    </Button>
                </div>
            </Card>
        </>
    )
}
